import React from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { exchanges } from './ExchangeSelector';
import { KuonMark } from './KuonLogo';
import {
  Container,
  Card,
  Eyebrow,
  PageTitle,
  CardTitle,
  Lead,
  PageHeader,
  Button,
  TextLink,
  IconCheck,
  IconMail,
  IconChevronLeft,
} from './ui';

const activeExchanges = exchanges.filter((exchange) => exchange.status === 'active');

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;

  @media (max-width: 820px) {
    grid-template-columns: 1fr;
  }
`;

const Stack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const TextCard = styled(Card)`
  display: flex;
  flex-direction: column;
  gap: 12px;

  p {
    font-size: 1rem;
    line-height: 1.6;
    color: var(--text-secondary);
  }
`;

const List = styled.ul`
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const ListItem = styled.li`
  display: flex;
  gap: 12px;
  font-size: 1rem;
  line-height: 1.5;
  color: var(--text-secondary);

  svg {
    flex-shrink: 0;
    margin-top: 2px;
    color: var(--accent);
  }
`;

const ChipList = styled.ul`
  list-style: none;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const Chip = styled.li`
  padding: 6px 12px;
  border-radius: 980px;
  background: var(--fill);
  font-size: 0.875rem;
  font-weight: 500;
  color: var(--text);
`;

const DetailsList = styled.dl`
  display: grid;
  grid-template-columns: minmax(140px, 220px) minmax(0, 1fr);
  margin-top: 8px;

  dt,
  dd {
    padding: 14px 0;
    border-top: 1px solid var(--separator);
    font-size: 0.9375rem;
  }

  dt {
    color: var(--text-secondary);
    padding-right: 16px;
  }

  dd {
    color: var(--text);
    font-weight: 500;
    overflow-wrap: anywhere;

    a {
      display: inline-flex;
      align-items: center;
      gap: 8px;
      color: var(--accent);
      text-decoration: none;

      &:hover {
        text-decoration: underline;
        text-underline-offset: 3px;
      }
    }
  }

  @media (max-width: 560px) {
    grid-template-columns: 1fr;

    dt {
      padding-bottom: 2px;
    }

    dd {
      padding-top: 0;
      border-top: 0;
    }
  }
`;

const ContactCard = styled(Card)`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 20px 32px;
  background: var(--surface);

  > div:first-child {
    max-width: 560px;
  }

  p {
    margin-top: 8px;
    color: var(--text-secondary);
    line-height: 1.55;
  }
`;

const Actions = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px 20px;
`;

const contactHref = (value) => {
  if (value.includes('@')) return `mailto:${value}`;
  if (/^\+?[\d\s()-]{7,}$/.test(value)) return `tel:${value.replace(/[^\d+]/g, '')}`;
  return null;
};

function AboutUs({ locale }) {
  const {
    eyebrow,
    title,
    missionText,
    fundingTitle,
    fundingText,
    platformTitle,
    platformList,
    supportedTitle,
    companyTitle,
    infoIntro,
    companyInfo,
    contactTitle,
    contactText,
    emailCta,
    backButtonLabel,
  } = locale;

  const email = companyInfo.find((item) => item.value.includes('@'))?.value;

  return (
    <Container>
      <PageHeader>
        <Eyebrow>{eyebrow}</Eyebrow>
        <PageTitle>{title}</PageTitle>
        <Lead>{missionText}</Lead>
      </PageHeader>

      <Stack>
        <Grid>
          <TextCard>
            <CardTitle>{fundingTitle}</CardTitle>
            <p>{fundingText}</p>
          </TextCard>

          <TextCard>
            <CardTitle>{platformTitle}</CardTitle>
            <List>
              {platformList.map((item) => (
                <ListItem key={item}>
                  <IconCheck size={18} strokeWidth={2.25} />
                  {item}
                </ListItem>
              ))}
            </List>
          </TextCard>
        </Grid>

        <TextCard>
          <CardTitle>{supportedTitle}</CardTitle>
          <ChipList>
            {activeExchanges.map((exchange) => (
              <Chip key={exchange.id}>{exchange.name}</Chip>
            ))}
          </ChipList>
        </TextCard>

        <TextCard>
          <CardTitle>{companyTitle}</CardTitle>
          <p>{infoIntro}</p>
          <DetailsList>
            {companyInfo.map((item) => {
              const href = item.href || contactHref(item.value);
              const external = Boolean(href && href.startsWith('http'));
              return (
                <React.Fragment key={item.label}>
                  <dt>{item.label}</dt>
                  <dd>
                    {href ? (
                      <a href={href} {...(external && { target: '_blank', rel: 'noopener' })}>
                        {item.brand === 'kuon' && <KuonMark size={18} />}
                        {item.value}
                      </a>
                    ) : (
                      item.value
                    )}
                  </dd>
                </React.Fragment>
              );
            })}
          </DetailsList>
        </TextCard>

        <ContactCard>
          <div>
            <CardTitle>{contactTitle}</CardTitle>
            <p>{contactText}</p>
          </div>
          <Actions>
            {email && (
              <Button as="a" href={`mailto:${email}`}>
                <IconMail size={18} />
                {emailCta}
              </Button>
            )}
            <TextLink as={Link} to="/">
              <IconChevronLeft size={16} strokeWidth={2} />
              {backButtonLabel}
            </TextLink>
          </Actions>
        </ContactCard>
      </Stack>
    </Container>
  );
}

export default AboutUs;
